import { create } from 'zustand';
import { getDataModel, type DataModel } from '../data/loadDataModel';
import { createSession } from '../features/session/createSession';
import { validateSession } from '../features/session/validateSession';
import { buildTestResult } from '../features/results/buildTestResult';
import { applyOptionOrder } from '../features/shuffle/shuffleOptions';
import {
  loadStoredData,
  saveStoredData,
  clearStoredSession,
} from '../features/persistence/storage';
import { DEFAULT_SETTINGS, type AppSettings } from '../utils/settingsLoader';
import { captureIdentityParams, type IdentityParams } from '../utils/identityParams';
import type { ContextFieldId, QuestionId, SessionStep } from '../types/ids';
import type { TestSession } from '../types/session';

export type ThemePreference = 'light' | 'dark' | 'system';

interface AppState {
  dataModel: DataModel;
  session?: TestSession;
  theme: ThemePreference;
  sessionWasDiscardedAsOutdated: boolean;
  settings: AppSettings;
  identity: IdentityParams;
}

interface AppActions {
  startNewSession: () => void;
  discardSession: (options?: { keepTheme?: boolean }) => void;
  goToStep: (step: SessionStep) => void;
  setCurrentQuestionIndex: (index: number) => void;
  setContextAnswer: (field: ContextFieldId, value: string) => void;
  setAnswer: (questionId: QuestionId, optionId: string) => void;
  computeAndStoreResult: () => void;
  setTheme: (theme: ThemePreference) => void;
  acknowledgeOutdatedSessionNotice: () => void;
  setSettings: (settings: AppSettings) => void;
}

export type AppStore = AppState & AppActions;

/**
 * Speichert sofort statt debounced (Abschnitt 28.3 erlaubt beides; bei den kleinen, seltenen
 * Session-Änderungen dieser App ist unmittelbares Schreiben sicherer als eine Debounce-Verzögerung,
 * die einen sehr schnellen Reload überholen könnte, siehe AC-012).
 */
function persist(session: TestSession | undefined, theme: ThemePreference): void {
  saveStoredData({ schemaVersion: '1.0.0', session, ui: { theme } }, true);
}

function loadInitialState(dataModel: DataModel): AppState {
  const stored = loadStoredData();
  const theme = stored?.ui.theme ?? 'system';
  const identity = captureIdentityParams();

  if (!stored?.session) {
    return {
      dataModel,
      theme,
      sessionWasDiscardedAsOutdated: false,
      settings: DEFAULT_SETTINGS,
      identity,
    };
  }

  const validation = validateSession(stored.session, {
    schemaVersion: dataModel.manifest.schemaVersion,
    scoringVersion: dataModel.manifest.scoringVersion,
  });

  if (!validation.valid) {
    return {
      dataModel,
      theme,
      sessionWasDiscardedAsOutdated: validation.reason !== 'structure',
      settings: DEFAULT_SETTINGS,
      identity,
    };
  }

  return {
    dataModel,
    session: validation.session,
    theme,
    sessionWasDiscardedAsOutdated: false,
    settings: DEFAULT_SETTINGS,
    identity,
  };
}

export const useAppStore = create<AppStore>((set, get) => {
  const dataModel = getDataModel();

  return {
    ...loadInitialState(dataModel),

    startNewSession: () => {
      const { dataModel: model, theme } = get();
      const session = createSession(
        model.questions,
        model.manifest.schemaVersion,
        model.manifest.scoringVersion,
      );
      set({ session, sessionWasDiscardedAsOutdated: false });
      persist(session, theme);
    },

    discardSession: (options) => {
      const keepTheme = options?.keepTheme ?? true;
      const theme = keepTheme ? get().theme : 'system';
      clearStoredSession(keepTheme, get().theme);
      set({ session: undefined, theme, sessionWasDiscardedAsOutdated: false });
    },

    goToStep: (step) => {
      const { session, theme } = get();
      if (!session) return;
      const updated: TestSession = {
        ...session,
        currentStep: step,
        updatedAt: new Date().toISOString(),
      };
      set({ session: updated });
      persist(updated, theme);
    },

    setCurrentQuestionIndex: (index) => {
      const { session, theme } = get();
      if (!session) return;
      const updated: TestSession = {
        ...session,
        currentQuestionIndex: index,
        updatedAt: new Date().toISOString(),
      };
      set({ session: updated });
      persist(updated, theme);
    },

    setContextAnswer: (field, value) => {
      const { session, theme } = get();
      if (!session) return;
      const updated: TestSession = {
        ...session,
        context: { ...session.context, [field]: value },
        updatedAt: new Date().toISOString(),
      };
      set({ session: updated });
      persist(updated, theme);
    },

    setAnswer: (questionId, optionId) => {
      const { session, theme, dataModel: model } = get();
      if (!session) return;
      const question = model.questions.find((q) => q.id === questionId);
      const option = question?.options.find((o) => o.id === optionId);
      if (!question || !option) return;
      const updated: TestSession = {
        ...session,
        answers: {
          ...session.answers,
          [questionId]: {
            questionId,
            optionId,
            score: option.score,
            answeredAt: new Date().toISOString(),
          },
        },
        updatedAt: new Date().toISOString(),
      };
      set({ session: updated });
      persist(updated, theme);
    },

    computeAndStoreResult: () => {
      const { session, theme, dataModel: model } = get();
      if (!session) return;
      const result = buildTestResult(session, model);
      const updated: TestSession = {
        ...session,
        result,
        currentStep: 'result',
        completedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      set({ session: updated });
      persist(updated, theme);
    },

    setTheme: (theme) => {
      const { session } = get();
      set({ theme });
      persist(session, theme);
    },

    acknowledgeOutdatedSessionNotice: () => set({ sessionWasDiscardedAsOutdated: false }),

    setSettings: (settings) => set({ settings }),
  };
});

/** Liefert die Optionen einer Frage in der für diese Session gemischten Reihenfolge. */
export function selectOrderedOptions(
  session: TestSession | undefined,
  questionId: QuestionId,
  dataModel: DataModel,
) {
  const question = dataModel.questions.find((q) => q.id === questionId);
  if (!question) return [];
  return applyOptionOrder(question, session?.optionOrders[questionId]);
}
